import React, { useState, useEffect, useRef } from "react";
import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  FaEdit,
  FaTrash,
  FaPlus,
  FaTimes,
  FaCheck,
  FaImage,
  FaSync,
  FaCalendarAlt,
  FaFilter,
  FaSearch,
  FaSort,
  FaExternalLinkAlt,
  FaUpload,
  FaSpinner,
} from "react-icons/fa";
import {
  MdCategory,
  MdOutlineDescription,
  MdSubdirectoryArrowRight,
} from "react-icons/md";

const SubCategories = () => {
  const [subCategories, setSubCategories] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    categoryId: "",
    subCategoryName: "",
    description: "",
    imageUrl: "",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const fileInputRef = useRef(null);

  // API base URLs
  const SUB_CATEGORY_API = `${process.env.REACT_APP_API_URL}/sub-category`;
  const CATEGORY_API = `${process.env.REACT_APP_API_URL}/service-category`;
  const CLOUDINARY_URL = `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_NAME}/upload`;

  // Fetch all categories and sub-categories
  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch categories
      const categoriesResponse = await axios.get(`${CATEGORY_API}/get-all`);
      if (categoriesResponse.data.success) {
        setCategories(categoriesResponse.data.data);
      }

      // Fetch sub-categories
      const subCategoriesResponse = await axios.get(
        `${SUB_CATEGORY_API}/get-all`
      );
      if (subCategoriesResponse.data.success) {
        setSubCategories(subCategoriesResponse.data.data);
      }
    } catch (err) {
      setError("Failed to fetch data");
      toast.error("Failed to fetch data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filter and sort sub-categories
  const filteredAndSortedSubCategories = subCategories
    .filter((subCategory) => {
      // Filter by category
      if (
        selectedCategoryFilter !== "all" &&
        subCategory.categoryId._id !== selectedCategoryFilter
      ) {
        return false;
      }

      // Filter by search term
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          subCategory.subCategoryName.toLowerCase().includes(searchLower) ||
          subCategory.description.toLowerCase().includes(searchLower) ||
          subCategory.categoryId.categoryName
            .toLowerCase()
            .includes(searchLower)
        );
      }

      return true;
    })
    .sort((a, b) => {
      if (sortConfig.key === "categoryName") {
        const categoryA = a.categoryId.categoryName.toLowerCase();
        const categoryB = b.categoryId.categoryName.toLowerCase();
        return sortConfig.direction === "asc"
          ? categoryA.localeCompare(categoryB)
          : categoryB.localeCompare(categoryA);
      }

      if (sortConfig.key === "subCategoryName") {
        const nameA = a.subCategoryName.toLowerCase();
        const nameB = b.subCategoryName.toLowerCase();
        return sortConfig.direction === "asc"
          ? nameA.localeCompare(nameB)
          : nameB.localeCompare(nameA);
      }

      // Sort by date
      const dateA = new Date(a[sortConfig.key]);
      const dateB = new Date(b[sortConfig.key]);
      return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
    });

  // Handle sort
  const handleSort = (key) => {
    setSortConfig({
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "desc"
          ? "asc"
          : "desc",
    });
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle file input change
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setFormData({
        ...formData,
        imageUrl: previewUrl,
      });
    }
  };

  // Upload image to Cloudinary
  const uploadToCloudinary = async (file) => {
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);
      formData.append('upload_preset', process.env.REACT_APP_UPLOAD_PRESET);
      formData.append('cloud_name', process.env.REACT_APP_CLOUDINARY_NAME);

      const response = await axios.post(CLOUDINARY_URL, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.secure_url) {
        return response.data.secure_url;
      }
      throw new Error('Upload failed');
    } catch (err) {
      console.error('Cloudinary upload error:', err);
      toast.error('Failed to upload image');
      throw err;
    } finally {
      setUploading(false);
    }
  };

  // Trigger file input click
  const handleUploadClick = () => {
    fileInputRef.current.click();
  };

  // Remove selected image
  const handleRemoveImage = () => {
    setImageFile(null);
    setFormData({
      ...formData,
      imageUrl: "",
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Open modal for adding new sub-category
  const handleAddNew = () => {
    if (categories.length === 0) {
      toast.warning("Please create a category first");
      return;
    }

    setFormData({
      categoryId: categories[0]?._id || "",
      subCategoryName: "",
      description: "",
      imageUrl: "",
    });
    setImageFile(null);
    setIsEditing(false);
    setIsModalOpen(true);
  };

  // Open modal for editing sub-category
  const handleEdit = (subCategory) => {
    setFormData({
      categoryId: subCategory.categoryId._id,
      subCategoryName: subCategory.subCategoryName,
      description: subCategory.description,
      imageUrl: subCategory.imageUrl,
    });
    setImageFile(null);
    setSelectedSubCategory(subCategory);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  // Open delete confirmation modal
  const handleDeleteClick = (subCategory) => {
    setSelectedSubCategory(subCategory);
    setIsDeleteModalOpen(true);
  };

  // Handle form submission (Create/Update)
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      let finalImageUrl = formData.imageUrl;

      // If there's a new image file, upload it to Cloudinary
      if (imageFile) {
        finalImageUrl = await uploadToCloudinary(imageFile);
      }

      const subCategoryData = {
        categoryId: formData.categoryId,
        subCategoryName: formData.subCategoryName,
        description: formData.description,
        imageUrl: finalImageUrl,
      };

      let response;
      if (isEditing) {
        // Update existing sub-category
        response = await axios.put(
          `${SUB_CATEGORY_API}/update/${selectedSubCategory._id}`,
          subCategoryData
        );
        
        if (response.data.success) {
          toast.success("Sub-category updated successfully!");
          fetchData();
          setIsModalOpen(false);
          setSelectedSubCategory(null);
          setImageFile(null);
        }
      } else {
        // Create new sub-category
        response = await axios.post(`${SUB_CATEGORY_API}/create`, subCategoryData);
        
        if (response.data.success) {
          toast.success("Sub-category created successfully!");
          fetchData();
          setIsModalOpen(false);
          setImageFile(null);
        }
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Operation failed");
      console.error(err);
    }
  };

  // Handle sub-category deletion
  const handleDelete = async () => {
    try {
      const response = await axios.delete(
        `${SUB_CATEGORY_API}/delete/${selectedSubCategory._id}`
      );
      
      if (response.data.success) {
        toast.success("Sub-category deleted successfully!");
        fetchData();
        setIsDeleteModalOpen(false);
        setSelectedSubCategory(null);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Delete failed");
      console.error(err);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="py-2 px-6 bg-gradient-to-br min-h-screen from-[#FFF7F3] to-gray-50">
        <Breadcrumb
          title1={"Sub Categories"}
          title2={"Sub Categories Management"}
          showBtn={true}
          buttonName={"Add New"}
          onBtnClick={handleAddNew}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          <div className="animate-pulse">
            <div className="h-12 bg-gray-200 rounded-lg mb-4"></div>
          </div>
          <div className="animate-pulse">
            <div className="h-12 bg-gray-200 rounded-lg mb-4"></div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
          {[...Array(6)].map((_, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-md overflow-hidden animate-pulse"
            >
              <div className="h-48 bg-gray-300"></div>
              <div className="p-6">
                <div className="h-6 bg-gray-300 rounded mb-4"></div>
                <div className="h-4 bg-gray-200 rounded mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="py-2 bg-gradient-to-br px-6 min-h-screen from-[#FFF7F3] to-gray-50">
      <ToastContainer position="top-right" autoClose={3000} />

      <Breadcrumb
        title1={"Sub Categories"}
        title2={"Sub Categories Management"}
        showBtn={true}
        buttonName={"Add New"}
        onBtnClick={handleAddNew}
      />

      {/* Search and Filter Section */}
      <div className="mb-8 bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
              <FaSearch className="h-4 w-4 mr-2 text-primaryColor" />
              Search Sub-Categories
            </label>
            <div className="relative">
              <input
                type="text"
                placeholder="Search by name, description, or category..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-10 py-3 border border-gray-300 rounded-xl focus:ring-1 focus:outline-none focus:ring-primaryColor focus:border-primaryColor transition-colors"
              />
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                <FaSearch className="h-5 w-5" />
              </div>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <FaTimes className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
              <FaFilter className="h-4 w-4 mr-2 text-primaryColor" />
              Filter by Category
            </label>
            <div className="relative">
              <select
                value={selectedCategoryFilter}
                onChange={(e) => setSelectedCategoryFilter(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-1 focus:ring-primaryColor focus:border-brandAccentColor transition-colors appearance-none bg-white"
              >
                <option value="all">All Categories</option>
                {categories.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.categoryName}
                  </option>
                ))}
              </select>
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
                <MdCategory className="h-5 w-5" />
              </div>
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
                <FaExternalLinkAlt className="h-4 w-4" />
              </div>
            </div>
          </div>

          {/* Sort Options */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
              <FaSort className="h-4 w-4 mr-2 text-primaryColor" />
              Sort By
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => handleSort("subCategoryName")}
                className={`px-4 py-3 rounded-xl flex items-center justify-center transition-all ${
                  sortConfig.key === "subCategoryName"
                    ? "bg-primaryColor text-white shadow-lg"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <FaSort className="h-4 w-4 mr-2" />
                Name{" "}
                {sortConfig.key === "subCategoryName" &&
                  (sortConfig.direction === "asc" ? "↑" : "↓")}
              </button>
              <button
                onClick={() => handleSort("createdAt")}
                className={`px-4 py-3 rounded-xl flex items-center justify-center transition-all ${
                  sortConfig.key === "createdAt"
                    ? "bg-primaryColor text-white shadow-lg"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                <FaCalendarAlt className="h-4 w-4 mr-2" />
                Date{" "}
                {sortConfig.key === "createdAt" &&
                  (sortConfig.direction === "asc" ? "↑" : "↓")}
              </button>
            </div>
          </div>
        </div>

        {searchTerm && (
          <p className="mt-4 text-sm text-gray-500 flex items-center">
            <FaSearch className="h-4 w-4 mr-2" />
            Found {filteredAndSortedSubCategories.length} sub-category
            {filteredAndSortedSubCategories.length !== 1 ? "s" : ""} matching "
            {searchTerm}"
          </p>
        )}
      </div>

      {error ? (
        <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-6">
          <div className="flex flex-col md:flex-row items-center justify-center text-red-600 gap-4">
            <p className="text-lg">{error}</p>
            <button
              onClick={fetchData}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center"
            >
              <FaSync className="h-4 w-4 mr-2" />
              Retry
            </button>
          </div>
        </div>
      ) : filteredAndSortedSubCategories.length === 0 ? (
        <div className="mt-12 text-center">
          <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
            <MdSubdirectoryArrowRight className="h-12 w-12 text-gray-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">
            {searchTerm || selectedCategoryFilter !== "all"
              ? "No matching sub-categories found"
              : "No sub-categories found"}
          </h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || selectedCategoryFilter !== "all"
              ? "Try adjusting your filters or search term"
              : "Get started by creating your first sub-category"}
          </p>
          <button
            onClick={handleAddNew}
            className="px-6 py-3 bg-gradient-to-r from-primaryColor to-brandAccentColor text-white rounded-xl hover:from-primaryColor hover:to-brandAccentColor transition-all shadow-lg hover:shadow-xl flex items-center mx-auto"
          >
            <FaPlus className="h-5 w-5 mr-2" />
            {searchTerm || selectedCategoryFilter !== "all"
              ? "Create New Sub-Category"
              : "Add Your First Sub-Category"}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredAndSortedSubCategories.map((subCategory) => (
            <div
              key={subCategory._id}
              className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-100 group"
            >
              {/* Sub-Category Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={subCategory.imageUrl}
                  alt={subCategory.subCategoryName}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    e.target.src =
                      "https://via.placeholder.com/400x300?text=No+Image";
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute top-3 right-3">
                  <span className="text-xs font-medium px-3 py-1 bg-green-100 text-green-700 rounded-full flex items-center">
                    <FaCheck className="h-3 w-3 mr-1" />
                    Active
                  </span>
                </div>
                <div className="absolute bottom-3 left-3">
                  <span className="text-xs font-semibold px-3 py-1 bg-primaryColor text-white rounded-full">
                    {subCategory.categoryId.categoryName}
                  </span>
                </div>
              </div>

              {/* Sub-Category Info */}
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center text-sm text-gray-500 mb-1">
                      <MdSubdirectoryArrowRight className="h-4 w-4 mr-2 text-primaryColor" />
                      Sub-Category of:{" "}
                      <span className="font-semibold text-primaryColor ml-1">
                        {subCategory.categoryId.categoryName}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-800 truncate group-hover:text-primaryColor transition-colors">
                      {subCategory.subCategoryName}
                    </h3>
                  </div>
                </div>

                <p className="text-gray-600 mb-4 line-clamp-2">
                  {subCategory.description}
                </p>

                {/* Action Buttons */}
                <div className="flex space-x-3">
                  <button
                    onClick={() => handleEdit(subCategory)}
                    className="flex-1 px-4 py-2 bg-gradient-to-r from-primaryColor to-brandAccentColor text-white rounded-lg hover:from-primaryColor hover:to-brandAccentColor transition-all shadow-md hover:shadow-lg flex items-center justify-center"
                  >
                    <FaEdit className="h-4 w-4 mr-2" />
                    Edit
                  </button>

                  <button
                    onClick={() => handleDeleteClick(subCategory)}
                    className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                    title="Delete"
                  >
                    <FaTrash className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 z-10 bg-gradient-to-r from-primaryColor via-brandAccentColor to-primaryColor border-b px-6 py-4 flex justify-between items-center">
              <div className="flex items-center">
                <div className="h-10 w-10 bg-brandAccentColor rounded-lg flex items-center justify-center mr-3">
                  {isEditing ? (
                    <FaEdit className="h-5 w-5 text-primaryColor" />
                  ) : (
                    <FaPlus className="h-5 w-5 text-primaryColor" />
                  )}
                </div>
                <h2 className="text-2xl font-bold text-white">
                  {isEditing ? "Edit Sub-Category" : "Add New Sub-Category"}
                </h2>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 hover:bg-brandAccentColor rounded-full transition-colors"
              >
                <FaTimes className="h-6 w-6 text-gray-100" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-6">
                {/* Category Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-100 mb-2 flex items-center">
                    <MdCategory className="h-4 w-4 mr-2 text-primaryColor" />
                    Parent Category *
                  </label>
                  <div className="relative">
                    <select
                      name="categoryId"
                      value={formData.categoryId}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-1 focus:outline-none focus:ring-primaryColor focus:border-primaryColor transition-colors appearance-none bg-white"
                    >
                      <option value="">Select a category</option>
                      {categories.map((category) => (
                        <option key={category._id} value={category._id}>
                          {category.categoryName}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
                      <FaExternalLinkAlt className="h-4 w-4" />
                    </div>
                  </div>
                </div>

                {/* Sub-Category Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <MdSubdirectoryArrowRight className="h-4 w-4 mr-2 text-primaryColor" />
                    Sub-Category Name *
                  </label>
                  <input
                    type="text"
                    name="subCategoryName"
                    value={formData.subCategoryName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-1 focus:outline-none focus:ring-primaryColor focus:border-primaryColor transition-colors"
                    placeholder="e.g., Hair Spa, Facial Treatment, etc."
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <MdOutlineDescription className="h-4 w-4 mr-2 text-primaryColor" />
                    Description *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    rows="4"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-1 focus:outline-none focus:ring-primaryColor focus:border-primaryColor transition-colors"
                    placeholder="Describe what this sub-category includes..."
                  />
                </div>

                {/* Image Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                    <FaImage className="h-4 w-4 mr-2 text-primaryColor" />
                    Sub-Category Image *
                  </label>

                  {/* Hidden file input */}
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept="image/*"
                    className="hidden"
                  />

                  {/* Upload area */}
                  {!formData.imageUrl ? (
                    <div
                      onClick={handleUploadClick}
                      className="w-full h-64 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-primaryColor hover:bg-blue-50 transition-colors"
                    >
                      <FaUpload className="h-12 w-12 text-gray-400 mb-4" />
                      <p className="text-gray-600 font-medium mb-1">
                        Click to upload image
                      </p>
                      <p className="text-sm text-gray-500">
                        PNG, JPG, GIF up to 10MB
                      </p>
                      {uploading && (
                        <div className="mt-4 flex items-center">
                          <FaSpinner className="h-4 w-4 text-primaryColor animate-spin mr-2" />
                          <span className="text-sm text-primaryColor">
                            Uploading...
                          </span>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <div className="relative w-full h-64 rounded-xl border overflow-hidden group">
                        <img
                          src={formData.imageUrl}
                          alt="Preview"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                          <button
                            type="button"
                            onClick={handleUploadClick}
                            className="opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all px-4 py-2 bg-white text-gray-700 rounded-lg shadow-md hover:shadow-lg"
                          >
                            Change Image
                          </button>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">
                          {imageFile ? imageFile.name : "Existing image"}
                        </span>
                        <button
                          type="button"
                          onClick={handleRemoveImage}
                          className="px-3 py-1 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-end space-y-4 sm:space-y-0 sm:space-x-4 mt-8 pt-6 border-t">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors flex items-center justify-center"
                >
                  <FaTimes className="h-5 w-5 mr-2" />
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading}
                  className="px-6 py-3 bg-primaryBtnColor hover:bg-primaryBtnHoverColor text-white rounded-xl transition-all shadow-lg hover:shadow-xl flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? (
                    <>
                      <FaSpinner className="h-5 w-5 mr-2 animate-spin" />
                      {isEditing ? "Updating..." : "Creating..."}
                    </>
                  ) : isEditing ? (
                    <>
                      <FaCheck className="h-5 w-5 mr-2" />
                      Update Sub-Category
                    </>
                  ) : (
                    <>
                      <FaPlus className="h-5 w-5 mr-2" />
                      Create Sub-Category
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && selectedSubCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center justify-center w-16 h-16 mx-auto bg-red-100 rounded-full mb-4">
                <FaTrash className="h-8 w-8 text-red-600" />
              </div>

              <h3 className="text-xl font-bold text-gray-800 text-center mb-2">
                Delete Sub-Category
              </h3>

              <p className="text-gray-600 text-center mb-4">
                Are you sure you want to delete "
                <span className="font-semibold text-gray-800">
                  {selectedSubCategory.subCategoryName}
                </span>
                "?
              </p>

              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
                <div className="flex items-start">
                  <FaTimes className="h-5 w-5 text-red-600 mr-2 mt-0.5" />
                  <p className="text-sm text-red-600">
                    This action cannot be undone. All services under this
                    sub-category will also be removed!
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors flex items-center justify-center"
                >
                  <FaTimes className="h-4 w-4 mr-2" />
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-xl hover:from-red-700 hover:to-red-800 transition-all flex items-center justify-center"
                >
                  <FaTrash className="h-4 w-4 mr-2" />
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubCategories;